import { Skeleton } from '@/components/ui/Skeleton.tsx';
import { TaggableType } from '@/features/dashboard/tags/types/TaggableType.ts';
import TagChip from '@/features/dashboard/tags/components/TagChip.tsx';
import TagPickerPopover from '@/features/dashboard/tags/components/TagPickerPopover.tsx';
import AddTagAffordance from '@/features/dashboard/tags/components/AddTagAffordance.tsx';
import useGetTargetTags from '@/features/dashboard/tags/hooks/useGetTargetTags.ts';
import useUnassignTag from '@/features/dashboard/tags/hooks/useUnassignTag.ts';

/**
 * Tagi rekordu jako zawijający się rząd kapsułek z przyciskiem dodawania —
 * do nagłówka ekranu, gdzie `TagPickerSection` z własną kartą i stale
 * otwartą wyszukiwarką zajmowałby więcej miejsca niż sam nagłówek.
 *
 * Kapsułki i przepływ dodawania to te same komponenty co w tabeli i sekcji:
 * `TagChip` i `TagPicker` w dymku. Nowy jest wyłącznie układ.
 */
const TagPickerRow = ({
  targetType,
  targetId,
}: {
  targetType: TaggableType;
  targetId: string;
}) => {
  const { data: tags = [], isLoading } = useGetTargetTags(targetType, targetId);
  const unassignMutation = useUnassignTag(targetType, targetId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5">
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-5 w-20 rounded-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tags.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          // `id` bywa puste w kształcie DTO, a bez niego nie ma czego odpiąć.
          onRemove={tag.id ? () => unassignMutation.mutate(tag.id!) : undefined}
        />
      ))}

      <TagPickerPopover targetType={targetType} targetId={targetId}>
        <button
          type="button"
          aria-label="Dodaj tagi"
          className="inline-flex items-center rounded-sm px-1 py-0.5 hover:bg-neutral-100"
        >
          {/* Przy pustej liście sam znaczek nie mówi, czego dotyczy — obok
              kapsułek mówi to już ich sąsiedztwo. */}
          <AddTagAffordance label={tags.length === 0 ? 'Tag' : undefined} />
        </button>
      </TagPickerPopover>
    </div>
  );
};

export default TagPickerRow;
